import { i18n } from '@fs/zion-locale'
import * as Yup from 'yup'
import { getRelationshipsBetweenTwoPeopleByType } from '@fs/zion-sls'
import { getDuplicatedRelationshipEvents } from './relationship.utils'
import { setNestedError } from '../../../../utils/formResolvers.utils'

/**
 * Dynamically finds the event object in the validation context by searching
 * for an object with event-specific properties (dates, places, eventType).
 * This is more resilient than hardcoded array indices.
 *
 * @param {Object} context - Yup validation context
 * @returns {Object|null} The event object if found, null otherwise
 */
const findEventInContext = (context) => {
  if (!context?.from) return null

  // Search through the context chain to find an object with event properties
  const eventContext = context.from.find((item) => {
    const value = item?.value
    // An event object should have dates, places, and optionally eventType
    return value && typeof value === 'object' && 'dates' in value && 'places' in value
  })

  return eventContext?.value || null
}

/**
 * Dynamically finds the root form values object by searching for
 * an object with an 'events' property containing 'primary' and 'additional' arrays.
 * This is more resilient than hardcoded array indices.
 *
 * @param {Object} context - Yup validation context
 * @returns {Object|null} The form values object if found, null otherwise
 */
const findFormValuesInContext = (context) => {
  if (!context?.from) return null

  // Search through the context chain
  const formContext = context.from.find((item) => {
    const value = item?.value
    // Form values object should have events.primary and events.additional
    return value && typeof value === 'object' && value.events?.primary && Array.isArray(value.events.primary)
  })

  return formContext?.value || null
}

/**
 * Yup validation schema for event form.
 *
 * Fields:
 * - eventType: Validates as an object, can be null. If all primaryEventTypes are falsy,
 *   this field becomes required. This applies to ALL persons regardless of person.primary flag.
 * - relationship.otherPerson: Not required anymore by FSS-13858. A test is done to ensure that
 *   if the relationship type is 'COUPLE', the selected otherPerson does not already have a 'PARENT_CHILD' relationship with the main person.
 *
 * @constant
 * @type {Yup.ObjectSchema}
 */
const eventSchemaShape = Yup.object().shape({
  eventType: Yup.object()
    .nullable()
    .test(
      'primary-event-required',
      i18n.t('eventsCard.validation.primaryEventRequired', 'At least one primary event is required.'),
      // eslint-disable-next-line func-names
      function validatePrimaryEventType(value) {
        const currentPath = this.path

        // Only apply this validation to primary events, not additional events
        if (!currentPath?.includes('events.primary')) {
          return true
        }

        // Use dynamic context search to get form values passed from React Hook Form
        // This ensures we're validating against the CURRENT form state, not stale context
        const formValues = findFormValuesInContext(this) || {}
        const primaryEvents = formValues?.events?.primary || []

        // Extract the current field's index from the path (e.g., "events.primary[0].eventType" -> 0)
        const match = /events\.primary\[(\d+)\]\.eventType/.exec(currentPath)
        const currentIndex = match ? Number.parseInt(match[1], 10) : -1

        // Check if all OTHER primary events have no eventType
        const otherPrimaryEventTypes = primaryEvents.filter((_, idx) => idx !== currentIndex).map((ev) => ev.eventType)

        // If there are other primary events and all of them have no type, THIS field is required
        if (otherPrimaryEventTypes.length > 0 && otherPrimaryEventTypes.every((ev) => !ev)) {
          return !!value
        }

        // If this is the only primary event, it's required
        if (primaryEvents.length === 1) {
          return !!value
        }

        return true
      }
    ),
  relationship: Yup.object()
    .shape({
      otherPerson: Yup.mixed()
        .nullable()
        .test(
          'no-couple-if-parent-child',
          i18n.t('eventsCard.validation.noCoupleIfParentChild', "Name can't be both a child and a spouse."),
          (value, testContext) => {
            const { eventType } = testContext.from[1]?.value || {}
            const person = testContext.options?.context?.person
            const relType = eventType?.relType
            const otherPersonId = value?.key

            if (relType === 'COUPLE' && otherPersonId && person) {
              const hasParentChild =
                getRelationshipsBetweenTwoPeopleByType({
                  person,
                  otherPersonId,
                  relType: 'PARENT_CHILD',
                })?.length > 0

              if (hasParentChild) {
                return false
              }
            }
            return true
          }
        )
        .test(
          'only-same-record-person',
          i18n.t(
            'eventsCard.validation.relationshipPersonSameRecord',
            'The selected person must belong to the same record as the main person.'
          ),
          (value, testContext) => {
            if (!value) return true

            const otherPersonRecord = value?.recordId
            const personRecordId = testContext.options?.context?.person?.recordId
            if (otherPersonRecord && personRecordId) {
              return otherPersonRecord === personRecordId
            }

            // If either recordId is missing, we can't validate, so we return false
            return false
          }
        ),
    })
    .nullable(),
})

/**
 * Factory function to create date/place field validation schemas.
 * Updated to work with simplified event structure where dates/places are arrays of field objects directly.
 *
 * @param {Object} config - Configuration object
 * @param {'DATE'|'PLACE'} config.fieldType - The type of field to validate
 * @param {boolean} config.isPrimary - Whether this is for primary events (always required) or additional events (conditionally required)
 * @returns {Yup.ObjectSchema} The validation schema for the field
 */
const createFieldSchema = ({ fieldType, isPrimary }) => {
  const testName = isPrimary
    ? `primary-${fieldType.toLowerCase()}-or-sibling-required`
    : `additional-${fieldType.toLowerCase()}-or-sibling-required-if-event-type`

  const errorMessage = i18n.t('eventsCard.validation.dateOrPlaceRequired', 'Date or Place is required.')

  return Yup.object().shape({
    value: Yup.mixed().test(testName, errorMessage, function validateField(value, testContext) {
      // Check if this field is the type we're validating
      if (testContext.parent.type !== fieldType) {
        return true
      }

      // Helper function to check if a field has a value
      const hasValue = (val) => Boolean(val?.originalText || val?.standard?.standardizedText)

      // Navigate up to the event object to access all fields
      // Use dynamic context search to find the event object
      const currentEvent = findEventInContext(testContext)

      if (!currentEvent) {
        // If we can't access the event, fall back to checking just this field
        const currentFieldHasValue = hasValue(value)
        return isPrimary ? currentFieldHasValue : true
      }

      // Check if ANY field of the current type (DATE or PLACE) has a value
      const currentFieldName = fieldType === 'DATE' ? 'dates' : 'places'
      const currentFieldArray = currentEvent[currentFieldName]
      const currentTypeHasValue = currentFieldArray?.some((field) => {
        if (field.type !== fieldType) {
          return false
        }
        return hasValue(field.value)
      })

      // Check if ANY sibling field (opposite type) has a value
      const siblingFieldName = fieldType === 'DATE' ? 'places' : 'dates'
      const siblingArray = currentEvent[siblingFieldName]
      const siblingHasValue = siblingArray?.some((field) => {
        if (field.type !== (fieldType === 'DATE' ? 'PLACE' : 'DATE')) {
          return false
        }
        return hasValue(field.value)
      })

      // For primary events, either current type OR sibling type must have value
      if (isPrimary) {
        return currentTypeHasValue || siblingHasValue
      }

      // For additional events, only require if eventType is selected
      if (currentEvent?.eventType) {
        return currentTypeHasValue || siblingHasValue
      }

      return true
    }),
  })
}

// Create schemas using the factory
const primaryDatesSchema = createFieldSchema({ fieldType: 'DATE', isPrimary: true })
const primaryPlacesSchema = createFieldSchema({ fieldType: 'PLACE', isPrimary: true })
const additionalDatesSchema = createFieldSchema({ fieldType: 'DATE', isPrimary: false })
const additionalPlacesSchema = createFieldSchema({ fieldType: 'PLACE', isPrimary: false })

const primaryEventSchemaShape = Yup.object().shape({
  ...eventSchemaShape.fields,
  dates: Yup.array().of(primaryDatesSchema),
  places: Yup.array().of(primaryPlacesSchema),
})

const additionalEventSchemaShape = Yup.object().shape({
  ...eventSchemaShape.fields,
  dates: Yup.array().of(additionalDatesSchema),
  places: Yup.array().of(additionalPlacesSchema),
})

/**
 * Yup validation schema for validating an object containing event arrays.
 *
 * The schema expects an object with an `events` property, which itself contains:
 * - `primary`: an array of objects validated by `eventSchema`
 * - `additional`: an array of objects validated by `eventSchema`
 *
 * Includes a custom test 'collect-primary-event-types' that collects the `eventType` of each primary event
 * and assigns the array to `context.primaryEventTypes` for validation of required primary events.
 *
 * @constant
 * @type {Yup.ObjectSchema}
 * @property {Object} events - The events object to validate.
 * @property {Array<Object>} events.primary - Array of primary event objects validated by `eventSchema`.
 * @property {Array<Object>} events.additional - Array of additional event objects validated by `eventSchema`.
 */
const basicEventsValidationSchema = Yup.object()
  .shape({
    events: Yup.object().shape({
      primary: Yup.array().of(primaryEventSchemaShape),
      additional: Yup.array().of(additionalEventSchemaShape),
    }),
  })
  .test('collect-primary-event-types', '', (value, testContext) => {
    const primaryEventTypes = value?.events?.primary?.map((ev) => ev.eventType)
    if (testContext.options.context) {
      testContext.options.context.primaryEventTypes = primaryEventTypes
    }
    return true
  })

/**
 * Validates the event form values using Yup and custom duplicate relationship logic.
 *
 * @async
 * @param {Object} formValues - The values from the event form to validate.
 * @param {Object} testContext - Additional context for validation, including the current person.
 * @param {Object} testContext.person - The person object used for duplicate relationship checks.
 * @returns {Promise<{values: Object, errors: Object}>} An object containing validated values (or empty object if errors) and a map of validation errors.
 */
export const eventsResolver = async (formValues, testContext) => {
  const errors = {}

  // Validate with Yup first (for all other simple errors)
  try {
    await basicEventsValidationSchema.validate(formValues, { abortEarly: false, context: testContext })
  } catch (yupError) {
    if (yupError.inner) {
      yupError.inner.forEach((err) => {
        if (err.path) {
          setNestedError(errors, err.path, err.message)
        }
      })
    }
  }

  // Now check for duplicate relationship events
  const primaryEventsLength = formValues?.events?.primary?.length || 0
  const { person } = testContext || {}
  const { duplicates, relationshipCombos } = getDuplicatedRelationshipEvents(formValues, person)
  const takenRelationshipIds = new Set()

  if (duplicates?.length > 0) {
    // Add an error for each duplicate
    duplicates.forEach((duplicate) => {
      const { position, isFromPrimaryList, combo, type } = duplicate
      const arrName = isFromPrimaryList ? 'primary' : 'additional'
      const arrIdx = isFromPrimaryList ? position : position - primaryEventsLength
      const basePath = `events.${arrName}[${arrIdx}].relationship`
      const relationshipId = relationshipCombos.find((relCombo) => relCombo.combo === combo)?.relationshipId

      // If the relationship does not have any events yet, we can add the current event to it.
      if (takenRelationshipIds.has(relationshipId) || type === 'EVENT') {
        const path = `${basePath}.otherPerson`
        setNestedError(
          errors,
          path,
          i18n.t(
            'eventsCard.validation.duplicateRelationship',
            'This relationship already exists between the selected persons.'
          )
        )
      } else {
        takenRelationshipIds.add(relationshipId)
      }
    })
  }

  return {
    values: Object.keys(errors).length === 0 ? formValues : {},
    errors,
  }
}
